import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Moon, Sun, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { navItems, filterNavByRole } from '@/config/nav'

export default function Sidebar({ onNavigate }) {
  const { user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const items = filterNavByRole(navItems, user)
  const [collapsed, setCollapsed] = useState(false)

  const roleLabel =
    typeof user?.role === 'string'
      ? user.role
      : user?.role?.slug || user?.role?.name || ''

  return (
    <aside className={cn(
      'flex h-full flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 ease-in-out',
      collapsed ? 'w-[60px]' : 'w-64'
    )}>

      {/* HEADER */}
      <div className="flex h-16 shrink-0 items-center border-b border-sidebar-border px-3 gap-2 overflow-hidden">
        <div className="h-8 w-8 shrink-0 rounded-md overflow-hidden">
          <img src={theme === 'dark' ? '/favicon.svg' : '/logoTr.svg'} alt="logo" className="h-full w-full object-cover" />
        </div>
        <span className={cn(
          'flex-1 text-lg font-semibold tracking-tight whitespace-nowrap transition-all duration-300',
          collapsed ? 'w-0 opacity-0 pointer-events-none' : 'opacity-100'
        )}>
          <span className={theme === 'dark' ? 'text-white' : 'text-black'}>Control </span>
          <span className="text-[#8e1373]">Salas</span>
        </span>
      </div>

      {/* NAV */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden px-2 py-4">
        {items.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-all duration-200',
                  'text-sidebar-foreground hover:bg-[#8e1373]/10 hover:text-[#8e1373]',
                  collapsed && 'justify-center',
                  isActive &&
                    'bg-gradient-to-r from-[#B81A97]/15 to-[#8E1374]/15 text-[#8e1373] before:absolute before:inset-y-1.5 before:left-0 before:w-1 before:rounded-full before:bg-[#8e1373]'
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className={cn(
                'truncate whitespace-nowrap transition-all duration-300 overflow-hidden',
                collapsed ? 'w-0 opacity-0' : 'w-full opacity-100'
              )}>
                {item.label}
              </span>
            </NavLink>
          )
        })}
      </nav>

      {/* FOOTER */}
      <div className="border-t border-sidebar-border p-3">
        <div className={cn(
          'flex gap-2',
          collapsed ? 'flex-col items-center' : 'flex-row items-center justify-end'
        )}>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-sidebar-border text-sidebar-foreground transition-colors hover:bg-[hsl(220_14%_93%)] hover:text-sidebar-foreground"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-sidebar-border text-sidebar-foreground transition-colors hover:bg-[hsl(220_14%_93%)] hover:text-sidebar-foreground"
            title={collapsed ? 'Expandir' : 'Colapsar'}
          >
            {collapsed
              ? <PanelLeftOpen className="h-4 w-4" />
              : <PanelLeftClose className="h-4 w-4" />
            }
          </button>
        </div>
      </div>

    </aside>
  )
}